import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { FiReplaceLinkTypes } from '@fi-shared/fi-directives/replace-with-links/replace-with-links.directive';
import { FloatLabelType } from '@angular/material/form-field';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { ILightWeightUser } from '@fi-shared/models';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'fi-contenteditable',
    template: `
        <div
            #contenteditableElRef
            class="content-editable branded-color-caret"
            [class.disabled]="disabled"
            contenteditable="true"
            [ngClass]="getEditorClassName()"
            [attr.not-empty]="!!ngModel?.length"
            [attr.placeholder]="placeholder"
            [fiAutofocus]="autofocus"
            [fiAutofocusTimeout]="autofocus ? 0 : undefined"
            [fiReplaceWithLinks]="replaceLinkTypes"
            (keydown)="onKeydown($event)"
            (keyup)="onKeyup($event)"
            (change)="onChange($event)"
            (copy)="onCopy($event)"
            (paste)="onPaste($event)"
            (blur)="onBlur($event)"
            (focus)="onFocus($event)"
            (click)="onClick($event)"
        ></div>
        @if (label) {
            <span
                class="label"
                [class.disabled]="disabled"
                [attr.float-label-type]="floatLabelType"
                (click)="focusEditor()"
                >{{ label }}</span
            >
        }
        @if (hint) {
            <span class="hint" [class.disabled]="disabled" (click)="focusEditor()">{{ hint }}</span>
        }
        @if (disabled) {
            <span class="disabled-overlay"></span>
        }
    `,
    styleUrls: ['./fi-contenteditable.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-contenteditable' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiContenteditableComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiContenteditableComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiContenteditableComponent extends ControlValueAccessorValidatorBase<string> implements OnChanges, OnInit {
    @Input() placeholder: string;
    @Input() autofocus: boolean;
    @Input() disabled: boolean;
    @Input() required: boolean;
    @Input() isSnackBackOnPasteShown: boolean = true;
    @Input() disableFormattingOnPaste: boolean;
    @Input() replaceLinkTypes: FiReplaceLinkTypes[] = [FiReplaceLinkTypes.Url, FiReplaceLinkTypes.Email];
    @Input() inputClassName: string;
    @Input() floatLabelType: FloatLabelType = 'auto';
    @Input() label: string;
    @Input() hint: string;
    @Input() taggedUsers: ILightWeightUser[] = [];

    @Output() focus = new EventEmitter<FocusEvent>();
    @Output() blur = new EventEmitter<FocusEvent>();
    @Output() filesPasted = new EventEmitter<File[]>();
    @Output() contentEditableClick = new EventEmitter<MouseEvent>();

    @ViewChild('contenteditableElRef', { static: true }) contenteditableElRef: ElementRef<HTMLElement>;
    private lastViewModel: string = ''; // used for preventing cursor issues
    @HostBinding('class.material-design') @Input() isMaterialDesign: boolean;

    constructor(
        private injector: Injector,
        public elementRef: ElementRef<HTMLElement>,
        private contenteditableService: FiContenteditableService,
        private textEditorService: TextEditorService,
        private textEditorTagUsersService: TextEditorTagUsersService,
    ) {
        super(injector);
    }

    public ngOnInit(): void {
        this.initializeUserTags(this.ngModel);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.ngModel;
        if (change && change.currentValue !== this.lastViewModel) {
            this.renderContent(this.ngModel || '');
        }
    }

    private initializeUserTags(content: string): void {
        const parsedContent = this.textEditorTagUsersService.getConvertedFromViewToEditModeUserTags(
            content,
            this.taggedUsers,
        );
        if (parsedContent === this.lastViewModel) {
            this.applyUserTagBehavior();
            return;
        }

        this.renderContent(parsedContent);
        this.applyUserTagBehavior();
    }

    private renderContent(content: string): void {
        this.contenteditableElRef.nativeElement.innerHTML = this.lastViewModel = content || '';
    }

    private applyUserTagBehavior(): void {
        this.textEditorTagUsersService.applyUserTagBehavior(this.contenteditableElRef.nativeElement, () =>
            this.doPropagateChanges(),
        );
    }

    protected getEditorClassName(): string {
        return this.inputClassName ? this.inputClassName : '';
    }

    protected onKeydown($event: KeyboardEvent): void {
        if (this.disabled) {
            $event.preventDefault();
            return;
        }

        this.textEditorService.handleUserTagBackspace($event, this.contenteditableElRef.nativeElement, () =>
            this.doPropagateChanges(),
        );
    }

    protected onKeyup($event: KeyboardEvent): void {
        this.doPropagateChanges();
    }

    protected onChange($event: Event): void {
        if (this.disabled) {
            $event.preventDefault();
            return;
        }
        this.doPropagateChanges();
    }

    protected onCopy($event: ClipboardEvent): void {
        this.contenteditableService.onCopy($event);
    }

    protected onPaste($event: ClipboardEvent): void {
        if (this.disabled) {
            $event.preventDefault();
            return;
        }

        this.contenteditableService
            .onPaste($event, {
                onImagesPasteCallback: (imageItems) => {
                    this.filesPasted.next(imageItems);
                },
                isSnackBarShown: this.isSnackBackOnPasteShown,
                disableFormatting: this.disableFormattingOnPaste,
            })
            .then(() => {
                setTimeout(() => this.doPropagateChanges());
            });
    }

    protected onBlur($event: FocusEvent): void {
        this.checkCleanHTML();
        this.blur.emit($event);
    }

    protected onFocus($event: FocusEvent): void {
        this.focus.emit($event);
    }

    protected focusEditor(): void {
        if (this.disabled) {
            return;
        }
        this.contenteditableElRef.nativeElement.focus();
    }

    public doPropagateChanges(): void {
        const innerHtml = this.contenteditableElRef.nativeElement.innerHTML;
        if (innerHtml !== this.lastViewModel) {
            this.ngModel = this.lastViewModel = innerHtml;
            super.doPropagateChanges();
        }
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        return {
            required: this.required && !this.ngModel,
        };
    }

    private checkCleanHTML() {
        const el = this.contenteditableElRef.nativeElement;
        if (
            (el.innerText.trim().length === 0 || el.innerText.match(/^\s+$/)) &&
            el.querySelectorAll('img').length === 0 &&
            el.querySelectorAll('video').length === 0 &&
            el.querySelectorAll('iframe').length === 0
        ) {
            el.innerText = '';
            this.doPropagateChanges();
        }
    }

    /*
        invoke this method after manipulations on selection like document.execCommand
     */
    public triggerChangeEvent(focusInput: boolean = true) {
        this.contenteditableElRef.nativeElement.dispatchEvent(new Event('change'));
        if (focusInput) this.contenteditableElRef.nativeElement.focus();
    }

    protected onClick($event: MouseEvent): void {
        this.contentEditableClick.emit($event);
    }
}
